import * as Redux from "redux";
import { useCookies } from "react-cookie";
import { QueryCondition, CoughAnalysisSet, Query } from "../misc/api/apiTypes";
import { MainState } from "../Core/reducers";
import { setCachedCoughAnalysisQuery } from "../misc/sessionStorageHelper";
import { Dispatch } from "redux";
import * as _ from 'lodash';
import { WebPortalLegacyClientInternal } from "../misc/api/WebPortalTokenClient";
import { WebPortalClient } from "webportalclient";

export enum ActionType {
    ClearAnalysisSets = "ClearAnalysisSets",
    LoadAnalysisSets = "LoadAnalysisSets",
    SortAnalysisSets = "SortAnalysisSets",
    FilterAnalysisSets = "FilterAnalysisSets",
    ClearAllFilters = "ClearAllFilters",
    PageAnalysisSets = "PageAnalysisSets",
    LoadCoughAnalysisSetTypes = "LoadCoughAnalysisSetTypes",
    LoadStudies = "LoadStudies",
    LoadSites = "LoadSites",
    LoadSubjects = "LoadSubjects",
    LoadSessions = "LoadSessions",
    LoadCountries = "LoadCountries",
    LoadDeviceSerials = "LoadDeviceSerials",
    LoadCardSerials = "LoadCardSerials",
    LoadWaveformTypes = "LoadWaveformTypes",
    LoadAnalysts = "LoadAnalysts",
    LoadOverreadStatuses = "LoadOverreadStatuses",    
    LoadPermissions = "LoadPermissions",
    LoadCurrentUser = "LoadCurrentUser",
    CacheAnalysisSets = "CacheAnalysisSets",    
    ToggleHistoryViewVisibility = "ToggleHistoryViewVisibility"    
}

export type Action =
    | ClearAnalysisSets
    | LoadAnalysisSetsAction
    | SortAnalysisSets
    | FilterAnalysisSets
    | ClearAllFiltersAction
    | LoadCoughAnalysisSetTypes
    | LoadSessions
    | LoadCountries
    | LoadDeviceSerials
    | LoadCardSerials
    | LoadWaveformTypes
    | LoadAnalysts
    | LoadOverreadStatuses
    | PageAnalysisSets
    | CacheAnalysisSets
    | ToggleHistoryViewVisibilityAction;

export class FetchAnalysisSetsAction {
    static create(legacyClient: WebPortalLegacyClientInternal, currentStudyId) {
        return (dispatch: (action: Redux.Action) => void, getState: () => MainState) => {
            dispatch(ClearAnalysisSets.create());

            FetchAnalysisSetsAction.FilterByCurrentStudyId(dispatch, currentStudyId);

            const state = getState();
            const query = { ...state.coughAnalysisScreen.cachedCoughAnalysisQuery, Data: null };

            legacyClient.getCoughAnalysisSets(query)
                .then(json => {
                    dispatch(LoadAnalysisSetsAction.create(json));
                });
        };
    }

    private static FilterByCurrentStudyId(dispatch: (action: Redux.Action) => void, currentStudy: string) {
        if (currentStudy)
            dispatch(FilterAnalysisSets.create("Study.Id", QueryCondition.Equal, currentStudy));
    }
}

export class FetchCoughAnalysisSetTypes {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return (dispatch: (action: Redux.Action) => void) => {
            legacyClient.getCoughAnalysisSetTypes()
                .then(json => {
                    dispatch(LoadCoughAnalysisSetTypes.create(json));
                });
        };
    }
}

export class FetchSessions {
    static create(legacyClient: WebPortalLegacyClientInternal) {
        return (dispatch: (action: Redux.Action) => void, getState: () => MainState) => {
            const state = getState();

            legacyClient.getCoughAnalysisSets({ ...state.coughAnalysisScreen.sessionQuery, Data: null })
                .then(json => {
                    dispatch(LoadSessions.create(json));
                });
        };
    }
}

export class ClearAnalysisSets implements Redux.Action {
    type: ActionType.ClearAnalysisSets;

    static create(): ClearAnalysisSets {
        return {
            type: ActionType.ClearAnalysisSets
        };
    }
}

export class LoadAnalysisSetsAction implements Redux.Action {
    type: ActionType.LoadAnalysisSets;
    data;

    static create(response): LoadAnalysisSetsAction {
        return {
            type: ActionType.LoadAnalysisSets,
            data: response
        };
    }
}

export class SortAnalysisSets implements Redux.Action {
    type: ActionType.SortAnalysisSets;
    member: string;

    static create(member: string): SortAnalysisSets {
        return {
            type: ActionType.SortAnalysisSets,
            member: member
        };
    }
}

export class CacheAnalysisSets implements Redux.Action {
    static create(query: Query<CoughAnalysisSet>): CacheAnalysisSets {
        return {
            type: ActionType.CacheAnalysisSets,
            query
        };
    }

    type: ActionType.CacheAnalysisSets;
    query: Query<CoughAnalysisSet>;
}

export class CacheAnalysisSetsAsync {
    static create() {
        return (dispatch: (action: Redux.Action) => void, getState: () => MainState) => {
            const state = getState();
            const cachedQuery = setCachedCoughAnalysisQuery(
                {
                    ...state.coughAnalysisScreen.coughAnalysisSetQuery,
                    Data: null
                });

            dispatch(CacheAnalysisSets.create(cachedQuery));
        };
    }
}

export class FilterAnalysisSets implements Redux.Action {
    type: ActionType.FilterAnalysisSets;
    member: string;
    condition: QueryCondition;
    value: any;

    static create(member: string, condition: QueryCondition, value: any): FilterAnalysisSets {
        return {
            type: ActionType.FilterAnalysisSets,
            member: member,
            condition: condition,
            value: value
        };
    }
}

export class ClearAllFiltersActionAsync {
    static create() {
        return (dispatch: Dispatch<MainState>) => {
            dispatch(ClearAllFiltersAction.create());
            dispatch(CacheAnalysisSetsAsync.create());
        };
    }
}

export class ClearAllFiltersAction implements Redux.Action {
    type: ActionType.ClearAllFilters;

    static create(): ClearAllFiltersAction {
        return {
            type: ActionType.ClearAllFilters
        };
    }
}

export class PageAnalysisSets implements Redux.Action {
    type: ActionType.PageAnalysisSets;
    page: number;

    static create(page: number): PageAnalysisSets {
        return {
            type: ActionType.PageAnalysisSets,
            page: page
        };
    }
}

export class LoadCoughAnalysisSetTypes implements Redux.Action {
    type: ActionType.LoadCoughAnalysisSetTypes;
    query: any;

    static create(response: any): LoadCoughAnalysisSetTypes {
        return {
            type: ActionType.LoadCoughAnalysisSetTypes,
            query: response
        };
    }
}

export class LoadSessions implements Redux.Action {
    type: ActionType.LoadSessions;
    query: any;

    static create(response: any): LoadSessions {
        return {
            type: ActionType.LoadSessions,
            query: response
        };
    }
}

export class LoadCountries implements Redux.Action {
    type: ActionType.LoadCountries;
    query: any;

    static create(response: any): LoadCountries {
        return {
            type: ActionType.LoadCountries,
            query: response
        };
    }
}

export class LoadDeviceSerials implements Redux.Action {
    type: ActionType.LoadDeviceSerials;
    query: any;

    static create(response: any): LoadDeviceSerials {
        return {
            type: ActionType.LoadDeviceSerials,
            query: response
        };
    }
}

export class LoadCardSerials implements Redux.Action {
    type: ActionType.LoadCardSerials;
    query: any;

    static create(response: any): LoadCardSerials {
        return {
            type: ActionType.LoadCardSerials,
            query: response
        };
    }
}

export class LoadWaveformTypes implements Redux.Action {
    type: ActionType.LoadWaveformTypes;
    query: any;

    static create(response: any): LoadWaveformTypes {
        return {
            type: ActionType.LoadWaveformTypes,
            query: response
        };
    }
}

export class LoadAnalysts implements Redux.Action {
    type: ActionType.LoadAnalysts;
    query: any;

    static create(response: any): LoadAnalysts {
        return {
            type: ActionType.LoadAnalysts,
            query: response
        };
    }
}

export class LoadOverreadStatuses implements Redux.Action {
    type: ActionType.LoadOverreadStatuses;
    query: any;

    static create(response: any): LoadOverreadStatuses {
        return {
            type: ActionType.LoadOverreadStatuses,
            query: response
        };
    }
}

export class ToggleHistoryViewVisibilityAction {
    type: ActionType.ToggleHistoryViewVisibility;
    analysisSetId: string;

    static create(analysisSetId?: string): ToggleHistoryViewVisibilityAction {
        return {
            type: ActionType.ToggleHistoryViewVisibility,
            analysisSetId: analysisSetId,
        };
    }
}
